import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useAnimationControls } from 'motion/react';
import { X, Sparkles, Ticket, Play, Coins, Plus, Minus, Heart, Loader2, Star } from 'lucide-react';
import LootBox from './LootBox';
import CurrencyIcon from './CurrencyIcon';
import { cn } from '../lib/utils';
import Confetti from './Confetti';
import { openBox, Reward } from '../utils/rewardSystem';
import { audioEngine } from '../utils/audioEngine';
import Prism3D from './Prism3D';
import { generatePrism } from '../utils/prismSystem';
import AnimaTicketIcon from './AnimaTicketIcon';

interface RewardBoxModalProps {
  tier: 'bronze' | 'silver' | 'gold' | 'diamond';
  maxCount: number;
  onClose: () => void;
  onClaim: (rewards: Reward[], count: number) => Promise<void>;
}

const TIER_CONFIG = {
  bronze: {
    color: 'hsl(30, 40%, 45%)',
    aura: 'rgba(180, 83, 9, 0.2)',
    rays: false,
    confetti: 80,
    shake: 1,
    glowIntensity: 1,
    badge: 'bg-orange-100 text-orange-600',
    openBg: 'bg-orange-500',
  },
  silver: {
    color: 'hsl(210, 10%, 75%)',
    aura: 'rgba(100, 116, 139, 0.3)',
    rays: false,
    confetti: 150,
    shake: 1.2,
    glowIntensity: 1.5,
    badge: 'bg-blue-100 text-blue-600',
    openBg: 'bg-blue-500',
  },
  gold: {
    color: 'hsl(45, 100%, 50%)',
    aura: 'rgba(251, 191, 36, 0.4)',
    rays: true,
    confetti: 250,
    shake: 1.6,
    glowIntensity: 2.5,
    badge: 'bg-yellow-100 text-yellow-600',
    openBg: 'bg-yellow-500',
  },
  diamond: {
    color: 'hsl(190, 90%, 65%)',
    aura: 'rgba(6, 182, 212, 0.4)',
    rays: true,
    confetti: 500,
    shake: 2.2,
    glowIntensity: 4,
    badge: 'bg-cyan-100 text-cyan-600',
    openBg: 'bg-cyan-600',
  },
};

const RARITY_WEIGHT = {
    common: 1,
    rare: 2,
    epic: 3,
    legendary: 4,
    jackpot: 5
};

const traitColorMap: Record<string, string> = {
  red: "#f43f5e", yellow: "#fbbf24", green: "#10b981", blue: "#38bdf8", violet: "#a855f7"
};

const potentialColorMap: Record<string, string> = {
  gc_multi: "#fbbf24",
  gc_bonus: "#fbbf24",
  exp_bonus: "#10b981",
  reward_multi: "#f97316",
  hybrid_bonus: "#f97316",
  reward_flat: "#6366f1",
  score_bonus: "#a855f7",
  fever_bonus: "#f43f5e",
  time_bonus: "#38bdf8"
};

const rewardColorMap: Record<string, string> = {
  coin_stack: '#fbbf24',
  starter_bolt: '#06b6d4',
  energy_pack: '#f43f5e',
  star_ticket: '#fbbf24',
  black_prism: '#1e293b'
};

function getRewardColor(reward: Reward): string {
  if (reward.type === 'prism') {
    // Ticket shouldn't reveal trait color. Return a neutral glow (or match ticket rarity color).
    return '#94a3b8'; // Neutral slate for Anima Tickets
  }
  return rewardColorMap[reward.type] || '#3b82f6';
}

interface GlowStyle {
  background: string;
  shadow: string;
}

function getRarityGlow(rarity: string): GlowStyle {
  if (rarity === 'jackpot') {
    return {
      background: 'linear-gradient(135deg, #ec4899, #8b5cf6, #3b82f6, #10b981)',
      shadow: 'rgba(236, 72, 153, 0.6)'
    };
  }
  if (rarity === 'legendary') {
    return {
      background: '#fbbf24', // gold
      shadow: 'rgba(251, 191, 36, 0.6)'
    };
  }
  if (rarity === 'epic') {
    return {
      background: '#a855f7', // violet
      shadow: 'rgba(168, 85, 247, 0.6)'
    };
  }
  if (rarity === 'rare') {
    return {
      background: '#38bdf8', // blue
      shadow: 'rgba(56, 189, 248, 0.6)'
    };
  }
  // normal / common
  return {
    background: '#ffffff', // white
    shadow: 'rgba(255, 255, 255, 0.4)'
  };
}

function RewardIcon({ reward, size = 'md', isStatic = false }: { reward: Reward; size?: 'sm' | 'md' | 'lg'; isStatic?: boolean }) {
  const cls = size === 'lg' ? 'w-16 h-16' : size === 'sm' ? 'w-6 h-6' : 'w-10 h-10';
  if (reward.type === 'coin_stack') return <CurrencyIcon type="coin" size={size === 'sm' ? 'sm' : 'lg'} />;
  if (reward.type === 'starter_bolt') return <CurrencyIcon type="gem" size={size === 'sm' ? 'sm' : 'lg'} />;
  if (reward.type === 'energy_pack') return <CurrencyIcon type="ticket_heart" size={size === 'sm' ? 'sm' : 'lg'} />;
  if (reward.type === 'prism') {
    return <AnimaTicketIcon stars={reward.stars || 1} size={size === 'sm' ? 'xs' : size === 'lg' ? 'lg' : 'sm'} />;
  }

  if (reward.type === 'black_prism') return <Sparkles className={cn(cls, 'text-slate-900')} />;
  if (reward.type === 'star_ticket') return <CurrencyIcon type="ticket_star" size={size === 'sm' ? 'sm' : 'lg'} />;
  return <div className="w-10 h-10 bg-slate-200 rounded-full" />;
}

function rarityStyle(rarity: string) {
  if (rarity === 'jackpot') return 'bg-yellow-100 text-yellow-600';
  if (rarity === 'legendary') return 'bg-amber-100 text-amber-600 border border-amber-300';
  if (rarity === 'epic') return 'bg-purple-100 text-purple-600';
  if (rarity === 'rare') return 'bg-blue-100 text-blue-500';
  return 'bg-slate-100 text-slate-400';
}

function aggregateRewards(rewards: Reward[]) {
  const coins = rewards.filter(r => r.type === 'coin_stack').reduce((s, r) => s + r.amount, 0);
  const gems = rewards.filter(r => r.type === 'starter_bolt').reduce((s, r) => s + r.amount, 0);
  const tickets = rewards.filter(r => r.type === 'energy_pack').reduce((s, r) => s + r.amount, 0);
  const prisms = rewards.filter(r => r.type === 'prism').reduce((s, r) => s + r.amount, 0);
  const blackPrisms = rewards.filter(r => r.type === 'black_prism').reduce((s, r) => s + r.amount, 0);
  const starTickets = rewards.filter(r => r.type === 'star_ticket').reduce((s, r) => s + r.amount, 0);

  return { coins, gems, tickets, prisms, blackPrisms, starTickets };
}

interface BoxAnimProps {
  tier: 'bronze' | 'silver' | 'gold' | 'diamond';
  count: number;
  preRolledRewards: Reward[];
  onDone: (rewards: Reward[]) => void;
}

function AnimatedBox({ tier, count, preRolledRewards, onDone }: BoxAnimProps) {
  const controls = useAnimationControls();
  const [isOpen, setIsOpen] = useState(false);
  const [isBeeping, setIsBeeping] = useState(false);
  const [showLightPillar, setShowLightPillar] = useState(false);
  const doneRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      // Entry delay
      await new Promise(r => setTimeout(r, 300));
      if (cancelled) return;

      // 3 Beeps with small shake
      for (let i = 0; i < 3; i++) {
        setIsBeeping(true);
        audioEngine.playSound('click'); 
        await controls.start({
          x: [-4, 4, -4, 4, 0],
          transition: { duration: 0.15 }
        });
        if (cancelled) return;
        setIsBeeping(false);
        await new Promise(r => setTimeout(r, 300)); // gap between beeps
        if (cancelled) return;
      }

      // Final anticipation pause
      await new Promise(r => setTimeout(r, 200));
      if (cancelled) return;

      // Pop open lid
      setIsOpen(true);
      audioEngine.playOpenBox();
      await controls.start({
        scale: [1, 1.05, 1],
        y: [0, -10, 0],
        transition: { duration: 0.25 }
      });
      if (cancelled) return;

      // Light pillar
      setShowLightPillar(true);
      await new Promise(r => setTimeout(r, 800)); // wait for pillar to fully rise
      if (cancelled) return;

      // Fade out
      await controls.start({
        opacity: 0,
        scale: 0.95,
        transition: { duration: 0.3 }
      });

      if (!doneRef.current) {
        doneRef.current = true;
        onDone(preRolledRewards);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [preRolledRewards, controls, onDone]);

  // Find the highest rarity in the pre-rolled rewards list
  const highestRarity = preRolledRewards.reduce((max, r) => {
    const maxVal = RARITY_WEIGHT[max] || 0;
    const rVal = RARITY_WEIGHT[r.rarity as keyof typeof RARITY_WEIGHT] || 0;
    return rVal > maxVal ? (r.rarity as any) : max;
  }, 'common');

  return (
    <motion.div
      initial={{ scale: 0.5, opacity: 0, y: 50 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      transition={{ duration: 0.5, type: 'spring', bounce: 0.4 }}
    >
      <motion.div animate={controls}>
        <LootBox 
          tier={tier} 
          isOpen={isOpen} 
          size="xl" 
          isBeeping={isBeeping} 
          showLightPillar={showLightPillar} 
          highestRarity={highestRarity}
        />
      </motion.div>
    </motion.div>
  );
}



export default function RewardBoxModal({ tier, maxCount, onClose, onClaim }: RewardBoxModalProps) {
  const [step, setStep] = useState<'pick' | 'opening' | 'revealing' | 'rewarded'>('pick');
  const config = TIER_CONFIG[tier];
  const capped = Math.max(0, Math.min(maxCount, 10));
  const [count, setCount] = useState(Math.max(0, Math.min(1, capped)));
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [preRolledRewards, setPreRolledRewards] = useState<Reward[]>([]);
  const [openedCount, setOpenedCount] = useState(0);
  const [revealIndex, setRevealIndex] = useState(0);

  const handleStartOpening = () => {
    const generated = Array.from({ length: count }).map(() => openBox(tier));
    const populated = generated.map(reward => {
      if (reward.type === 'prism' && !(reward as any).instance) {
        (reward as any).instance = generatePrism(reward.stars || 1, reward.color);
      }
      return reward;
    });
    setPreRolledRewards(populated);
    setRewards([]);
    setOpenedCount(0);
    setRevealIndex(0);
    setStep('opening');
  };

  const claimingRef = useRef(false);

  useEffect(() => {
    if (rewards.length === count && count > 0 && !claimingRef.current && step === 'opening') {
      claimingRef.current = true;
      
      // SORT BY RARITY: Common -> Rare -> Epic -> Jackpot
      const sorted = [...rewards].sort((a, b) => 
          (RARITY_WEIGHT[a.rarity as keyof typeof RARITY_WEIGHT] || 0) - 
          (RARITY_WEIGHT[b.rarity as keyof typeof RARITY_WEIGHT] || 0)
      );

      onClaim(sorted, count).then(() => {
        setRewards(sorted);
        setRevealIndex(0);
        setStep('revealing');
      }).catch(err => {
        console.error("Failed to claim rewards:", err);
        claimingRef.current = false; // Allow retry or handle error
      });
    }
  }, [rewards.length, count, onClaim, step]);

  const handleAllBoxesDone = (generated: Reward[]) => {
    const populated = generated.map(reward => {
      // Generate actual instance for visual display if it's a prism
      if (reward.type === 'prism' && !(reward as any).instance) {
        (reward as any).instance = generatePrism(reward.stars || 1, reward.color);
      }
      return reward;
    });
    setRewards(populated);
    setOpenedCount(populated.length);
  };


  const agg = aggregateRewards(rewards);

  return (
    <div className="absolute inset-0 z-[2000] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 bg-slate-900/80 backdrop-blur-md"
        onClick={step === 'rewarded' ? onClose : undefined}
      />

      <motion.div
        initial={{ scale: 0.9, opacity: 0, y: 40 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.9, opacity: 0, y: 40 }}
        className="w-full max-w-md p-4 sm:p-6 relative z-10"
      >
        <AnimatePresence mode="wait">
          {step === 'pick' && (
            <motion.div key="pick" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="game-card-3d border-none bg-white overflow-hidden p-7 flex flex-col items-center gap-6 rounded-3xl">
              <button onClick={onClose} className="absolute top-5 right-5 p-2 hover:bg-slate-100 rounded-full transition-colors"><X className="w-5 h-5 text-slate-400" /></button>
              <LootBox tier={tier} size="lg" />
              <div className="text-center">
                <h2 className="text-3xl font-num uppercase tracking-tight text-slate-900">{tier} Box</h2>
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mt-1">Stock: {maxCount}</p>
              </div>
              <div className="flex items-center gap-4">
                <button onClick={() => setCount(c => Math.max(0, c - 1))} className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center toy-3d-button border-none disabled:opacity-30"><Minus className="w-4 h-4 text-slate-700" /></button>
                <div className="w-16 h-16 rounded-2xl bg-slate-50 flex items-center justify-center clay-base border-none shadow-inner"><span className="text-3xl font-num font-black text-slate-900">{count}</span></div>
                <button onClick={() => setCount(c => Math.min(capped, c + 1))} className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center toy-3d-button border-none disabled:opacity-30"><Plus className="w-4 h-4 text-slate-700" /></button>
              </div>

              {capped > 1 && (
                <button 
                  onClick={() => setCount(capped)}
                  className="px-6 py-2 bg-slate-50 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-slate-100 transition-colors border-none -mt-2"
                >
                  Set {capped}x
                </button>
              )}

              <button onClick={handleStartOpening} disabled={count <= 0} className="toy-3d-button border-none w-full font-num text-xl py-5 bg-[#fbbf24] text-slate-900 disabled:opacity-50">OPEN {count}x</button>
            </motion.div>
          )}

          {step === 'opening' && (
            <motion.div key="opening" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="p-7 flex flex-col items-center gap-6 min-h-[380px]">
              <h2 className="text-3xl font-num uppercase tracking-tight text-white">
                {claimingRef.current ? "Claiming..." : "Opening..."}
              </h2>
              {claimingRef.current ? (
                <div className="flex flex-col items-center gap-4 py-12">
                   <Loader2 className="w-12 h-12 animate-spin text-[#fbbf24]" />
                   <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Securing your loot...</p>
                </div>
              ) : (
                <div className="flex justify-center items-center w-full min-h-[300px]">
                  <AnimatedBox tier={tier} count={count} preRolledRewards={preRolledRewards} onDone={handleAllBoxesDone} />
                </div>
              )}
            </motion.div>
          )}

          {step === 'revealing' && rewards[revealIndex] && (
            <motion.div
              key="revealing-wrapper"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="p-10 flex flex-col items-center justify-between min-h-[420px] w-full cursor-pointer select-none"
              onClick={() => revealIndex + 1 < rewards.length ? setRevealIndex(i => i + 1) : setStep('rewarded')}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={`reveal-item-${revealIndex}`}
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 1.1, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col items-center gap-6 w-full"
                >
                  <div className="relative">
                    {(() => {
                      const glow = getRarityGlow(rewards[revealIndex].rarity);
                      return (
                        <motion.div
                            animate={{ scale: [1, 1.25, 1], opacity: [0.4, 0.8, 0.4] }}
                            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                            className="absolute inset-0 rounded-[40px] blur-2xl -z-10"
                            style={{ background: glow.background }}
                        />
                      );
                    })()}
                    <div 
                      className="p-8 bg-white/10 backdrop-blur-md rounded-[40px] flex flex-col items-center gap-3 border border-white/20"
                      style={{
                        boxShadow: `0 12px 32px -8px ${getRarityGlow(rewards[revealIndex].rarity).shadow}, 0 0 20px 2px ${getRarityGlow(rewards[revealIndex].rarity).shadow}`
                      }}
                    >
                        {/* Stars */}
                        {rewards[revealIndex].type !== 'prism' && (rewards[revealIndex] as any).instance && (
                          <div className="flex gap-1">
                            {Array.from({ length: (rewards[revealIndex] as any).instance.stars }).map((_, i) => (
                              <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                            ))}
                          </div>
                        )}
                        
                        <RewardIcon reward={rewards[revealIndex]} size="lg" />

                        {/* Stat Tray */}
                        {rewards[revealIndex].type !== 'prism' && (rewards[revealIndex] as any).instance && (
                          <div className="mt-2 w-full bg-white/10 rounded-xl p-1.5 flex items-center justify-center gap-1.5 border border-white/10 shadow-inner">
                            <div className="w-6 h-6 rounded-lg flex items-center justify-center shadow-sm" style={{ backgroundColor: traitColorMap[(rewards[revealIndex] as any).instance.color] }}>
                              <Star className="w-3.5 h-3.5 fill-white text-white" />
                            </div>
                            <div className="w-[1px] h-4 bg-white/20 mx-1" />
                            {[(rewards[revealIndex] as any).instance.slot1, (rewards[revealIndex] as any).instance.slot2, (rewards[revealIndex] as any).instance.slot3].filter(Boolean).map((pot, i) => (
                              <div key={i} className="w-6 h-6 rounded-lg flex items-center justify-center shadow-sm" style={{ backgroundColor: potentialColorMap[pot!.type] }} />
                            ))}
                          </div>
                        )}
                    </div>
                  </div>

                  <div className="text-center flex flex-col items-center gap-2.5">
                    {['coin_stack', 'starter_bolt', 'energy_pack', 'star_ticket'].includes(rewards[revealIndex].type) && (
                      <span className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-300 mb-1">
                        {rewards[revealIndex].type === 'coin_stack' ? 'Coin' :
                         rewards[revealIndex].type === 'starter_bolt' ? 'Hoshi' :
                         rewards[revealIndex].type === 'energy_pack' ? 'Heart Ticket' :
                         'Star Ticket'}
                      </span>
                    )}
                    <div className={cn("px-4 py-1 rounded-full text-[10px] font-black uppercase inline-block", rarityStyle(rewards[revealIndex].rarity))}>
                      {rewards[revealIndex].rarity}
                    </div>
                    {['coin_stack', 'starter_bolt', 'energy_pack', 'star_ticket'].includes(rewards[revealIndex].type) && (
                      <h2 className="text-5xl font-num text-white leading-tight mt-2.5 flex items-center justify-center">
                        {['energy_pack', 'star_ticket'].includes(rewards[revealIndex].type) && (
                          <span className="text-2xl font-black text-slate-400 mr-1.5 select-none">x</span>
                        )}
                        {rewards[revealIndex].amount.toLocaleString()}
                      </h2>
                    )}
                  </div>
                </motion.div>
              </AnimatePresence>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-4">({revealIndex + 1}/{rewards.length}) Tap to Continue</p>
            </motion.div>
          )}

          {step === 'rewarded' && (
            <motion.div key="rewarded" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="px-2 py-6 sm:px-4 flex flex-col items-center gap-5 w-full">
              <Confetti active={step === 'rewarded'} count={Math.min(config.confetti, 150)} />
              <div className="w-full grid grid-cols-4 gap-2 sm:gap-2.5 max-h-80 overflow-y-auto p-1">
                {rewards.map((r, i) => {
                  const isTicket = ['energy_pack', 'star_ticket'].includes(r.type);
                  const isCurrency = ['coin_stack', 'starter_bolt'].includes(r.type);
                  const isAnima = r.type === 'prism';
                  const p = isAnima ? (r as any).instance : null;
                  
                  let cellLabel = '';
                  if (isCurrency) cellLabel = r.amount.toLocaleString();
                  else if (isTicket) cellLabel = `x${r.amount}`;
                  else if (r.type === 'black_prism') cellLabel = 'Black';

                  return (
                    <div 
                      key={i} 
                      className="aspect-square bg-white/10 rounded-2xl flex flex-col items-center justify-center p-1 sm:p-2 relative border border-white/10 shadow-sm hover:scale-102 transition-transform"
                      style={{
                        boxShadow: `0 4px 10px -4px ${getRarityGlow(r.rarity).shadow}`
                      }}
                    >
                      <div className="w-10 h-10 flex items-center justify-center mb-0.5">
                        <RewardIcon reward={r} size="md" isStatic={true} />
                      </div>
                      
                      {!isAnima && p ? (
                        <div className="w-full flex items-center justify-center gap-0.5 mt-1 p-0.5 bg-white/10 rounded-lg border border-white/20">
                          <div 
                            className="w-[12px] h-[12px] sm:w-[15px] sm:h-[15px] rounded-md flex items-center justify-center shadow-sm shrink-0" 
                            style={{ backgroundColor: traitColorMap[p.color] }}
                          >
                            <Star className="w-2 h-2 sm:w-2.5 sm:h-2.5 fill-white text-white" />
                          </div>
                          <div className="w-[1px] h-3 bg-white/20 mx-0.5 shrink-0" />
                          {[p.slot1, p.slot2, p.slot3].filter(Boolean).map((pot, idx) => (
                            <div 
                              key={idx} 
                              className="w-[12px] h-[12px] sm:w-[15px] sm:h-[15px] rounded-md flex items-center justify-center shadow-sm shrink-0" 
                              style={{ backgroundColor: potentialColorMap[pot!.type] }}
                            />
                          ))}
                        </div>
                      ) : (
                        <span className="text-[9px] sm:text-[10px] font-black uppercase text-white text-center leading-none truncate w-full px-0.5 mt-1.5">
                          {isAnima ? `${r.stars || 1}★ Ticket` : cellLabel}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
              <button 
                onClick={() => {
                  console.log("COLLECT ALL button clicked inside RewardBoxModal!");
                  audioEngine.playSound('click');
                  onClose();
                }} 
                className="toy-3d-button border-none w-full bg-[#fbbf24] text-slate-900 font-num text-xl py-5 mt-4 cursor-pointer relative z-50 hover:brightness-105 active:scale-[0.98] transition-all"
              >
                COLLECT ALL
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
